import { useEffect, useState, type ReactNode } from "react"
import type { SortingState } from "@tanstack/react-table"

import { api, type ContratoNaLista, type Meta } from "../api"
import { Badge } from "../componentes/Badge"
import { Button } from "../componentes/Button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Escolha } from "../componentes/Escolha"
import * as Icones from "../componentes/Icones"
import { Paginacao } from "../componentes/Paginacao"
import { FalhaNaSecao } from "../componentes/Secao"
import { Selo, SeloDoValor } from "../componentes/Selo"
import { Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { EsqueletoDeTabela } from "../ficha/Pedido"
import { COLUNA_NUMERICA } from "../ficha/Tabela"
import { data, inteiro, moeda, processo as numeroDoProcesso, numeroComAno, rotulo } from "../formato"
import { TEXTO_DO_CADASTRO } from "../lib/classes"
import { cn } from "../lib/cn"
import { useIsMobile } from "../lib/use-is-mobile"
import { consultaDoRecorte, type RecorteDaLista } from "../recortes"

type Props = {
  aoAbrirContrato: (id: number) => void
  recorte: RecorteDaLista | null
  aoLimparRecorte: () => void
}

const SITUACOES = ["vigente", "suspenso", "encerrado", "rescindido", "minuta", "assinado"]
const TIPOS = ["servico_continuo", "fornecimento_continuo", "obra", "reforma", "fornecimento", "servico_escopo"]

const ORDEM_PADRAO = "vigencia_fim"
const FILTRO_DA_BARRA = "w-full min-w-0 md:w-48 md:flex-none"

export function Contratos({ aoAbrirContrato, recorte, aoLimparRecorte }: Props) {
  const movel = useIsMobile()
  const [dados, definirDados] = useState<ContratoNaLista[] | null>(null)
  const [meta, definirMeta] = useState<Meta | null>(null)
  const [erro, definirErro] = useState<string | null>(null)
  const [vez, definirVez] = useState(0)

  const [busca, definirBusca] = useState("")
  const [situacao, definirSituacao] = useState("")
  const [tipo, definirTipo] = useState("")
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(20)
  const [ordenarPor, definirOrdenarPor] = useState(ORDEM_PADRAO)
  const [ordem, definirOrdem] = useState<"asc" | "desc">("asc")

  useEffect(() => {
    definirPagina(1)
  }, [recorte])

  useEffect(() => {
    let vivo = true
    const parametros = new URLSearchParams({
      pagina: String(pagina),
      por_pagina: String(porPagina),
      ordenar_por: ordenarPor,
      ordem,
    })
    if (busca.trim() !== "") parametros.set("busca", busca.trim())
    if (situacao !== "") parametros.set("situacao", situacao)
    if (tipo !== "") parametros.set("tipo", tipo)
    for (const [chave, valor] of new URLSearchParams(consultaDoRecorte(recorte))) {
      parametros.set(chave, valor)
    }

    definirErro(null)
    api
      .contratos(`?${parametros.toString()}`)
      .then((resposta) => {
        if (!vivo) return
        definirDados(resposta.dados)
        definirMeta(resposta.meta)
      })
      .catch((falha: unknown) => {
        if (vivo) definirErro(falha instanceof Error ? falha.message : "falha ao carregar")
      })
    return () => {
      vivo = false
    }
  }, [busca, situacao, tipo, pagina, porPagina, ordenarPor, ordem, recorte, vez])

  const mostrarOrigem = new Set((dados ?? []).map((contrato) => contrato.origem ?? "proprio")).size > 1
  const filtrado = busca.trim() !== "" || situacao !== "" || tipo !== ""

  const trocar = (mudar: () => void) => {
    mudar()
    definirPagina(1)
  }
  const limparFiltros = () =>
    trocar(() => {
      definirBusca("")
      definirSituacao("")
      definirTipo("")
    })

  const buscaNaBarra = (
    <TablePrincipalFiltroGlobal
      globalFilter={busca}
      setGlobalFilter={(valor) => trocar(() => definirBusca(valor))}
      placeholder="Número, objeto ou contratado"
    />
  )
  const filtros = (
    <>
      <Escolha
        rotulo="Filtrar pela situação"
        classeDaCaixa={FILTRO_DA_BARRA}
        valor={situacao}
        aoMudar={(valor) => trocar(() => definirSituacao(valor))}
        vazio="Todas as situações"
        opcoes={SITUACOES.map((s) => ({ valor: s, rotulo: rotulo(s) }))}
      />
      <Escolha
        rotulo="Filtrar pelo tipo"
        classeDaCaixa={FILTRO_DA_BARRA}
        valor={tipo}
        aoMudar={(valor) => trocar(() => definirTipo(valor))}
        vazio="Todos os tipos"
        opcoes={TIPOS.map((t) => ({ valor: t, rotulo: rotulo(t) }))}
      />
    </>
  )

  const filtrosDescritos = [
    busca.trim() !== "" && `com a busca "${busca.trim()}"`,
    situacao !== "" && `na situação ${rotulo(situacao)}`,
    tipo !== "" && `do tipo ${rotulo(tipo)}`,
  ].filter(Boolean)
  const vazio = (
    <Empty className="py-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icones.Contrato />
        </EmptyMedia>
        <EmptyTitle>
          {recorte !== null
            ? `Nenhum contrato atende ao recorte "${recorte.rotulo}"`
            : filtrado
              ? "Nenhum contrato atende a estes filtros"
              : "Nenhum contrato cadastrado"}
        </EmptyTitle>
        {filtrosDescritos.length > 0 && (
          <EmptyDescription className="first-letter:uppercase">{`${filtrosDescritos.join(", ")}.`}</EmptyDescription>
        )}
      </EmptyHeader>
      {filtrado && (
        <EmptyContent>
          <Button type="button" variant="outline" size="sm" onClick={limparFiltros}>
            <Icones.Fechar />
            Limpar filtros
          </Button>
        </EmptyContent>
      )}
    </Empty>
  )

  const ordenacao: SortingState = [{ id: ordenarPor, desc: ordem === "desc" }]
  const avisos: ReactNode = (
    <>
      {recorte !== null &&
        meta !== null &&
        recorte.esperado !== null &&
        recorte.esperado !== undefined &&
        recorte.esperado !== meta.total && (
          <p className="m-0 pb-3 text-xs font-semibold text-foreground">
            O painel contou {inteiro(recorte.esperado)} e a lista tem {inteiro(meta.total)}. Confira antes de agir sobre esta lista.
          </p>
        )}
      {meta !== null && meta.avisos.length > 0 && (
        <div className="mb-3 rounded-md bg-(--amarelo-fundo) px-3 py-2 text-xs text-foreground [&>p]:my-0.5" role="status">
          {meta.avisos.map((aviso) => (
            <p key={aviso}>O servidor avisa: {aviso}</p>
          ))}
        </div>
      )}
    </>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contratos</CardTitle>
        {recorte !== null && (
          <CardAction>
            <Badge variant="secondary" className="h-7 gap-1.5 border-(--azul-destaque) py-0 pr-0.5 pl-2">
              <Icones.Etiqueta />
              <span className="max-w-[22rem] truncate" title={recorte.rotulo}>
                {recorte.rotulo}
              </span>
              <button
                type="button"
                title="Limpar o recorte"
                aria-label="Limpar o recorte"
                className="inline-flex size-6 cursor-pointer items-center justify-center rounded-sm hover:bg-(--borda) focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--foco)"
                onClick={aoLimparRecorte}
              >
                <Icones.Fechar />
              </button>
            </Badge>
          </CardAction>
        )}
      </CardHeader>

      <CardContent>
        {avisos}
        {erro !== null ? (
          <FalhaNaSecao mensagem={`Não foi possível carregar os contratos: ${erro}`} aoTentar={() => definirVez((v) => v + 1)} />
        ) : dados === null ? (
          <EsqueletoDeTabela linhas={5} />
        ) : movel ? (
          <div className="flex flex-col gap-3">
            <div data-slot="filtros" className="flex flex-wrap items-center gap-2">
              {buscaNaBarra}
              {filtros}
            </div>
            {dados.length === 0 ? (
              vazio
            ) : (
              <ContratosEmCartoes dados={dados} mostrarOrigem={mostrarOrigem} aoAbrirContrato={aoAbrirContrato} />
            )}
            {meta !== null && (
              <Paginacao
                className="border-t border-border pt-3"
                pagina={meta.pagina}
                porPagina={meta.por_pagina}
                total={meta.total}
                aoTrocarPagina={definirPagina}
                aoTrocarPorPagina={(quantos) => trocar(() => definirPorPagina(quantos))}
              />
            )}
          </div>
        ) : (
          <TablePrincipal2<ContratoNaLista>
            data={dados}
            listrada
            pageCount={Math.max(1, meta?.paginas ?? 1)}
            currentPage={meta?.pagina ?? pagina}
            pageSize={porPagina}
            totalRegistros={meta?.total ?? 0}
            onPageChange={definirPagina}
            onPageSizeChange={(quantos) => trocar(() => definirPorPagina(quantos))}
            ordenacaoNoServidor
            ordenacao={ordenacao}
            onSortingChange={(nova) => {
              const primeira = nova[0]
              trocar(() => {
                definirOrdenarPor(primeira === undefined ? ORDEM_PADRAO : primeira.id)
                definirOrdem(primeira?.desc === true ? "desc" : "asc")
              })
            }}
            globalFilterValue={busca}
            onGlobalFilterChange={(valor) => trocar(() => definirBusca(valor))}
            filtrosNaBarra={filtros}
            mensagemVazia={vazio}
            aoClicarNaLinha={(contrato) => aoAbrirContrato(contrato.id)}
          >
            {buscaNaBarra}
            {mostrarOrigem && (
              <Column<ContratoNaLista>
                name="origem"
                label="De onde veio"
                enableSorting={false}
                render={(_v, contrato) =>
                  contrato.origem === "athus" ? (
                    <Selo
                      situacao="origem_athus"
                      titulo={
                        contrato.origem_processo !== null
                          ? `Veio do ATHUS · processo ${numeroDoProcesso(contrato.origem_processo)}`
                          : "Veio do ATHUS"
                      }
                    />
                  ) : (
                    <Selo situacao="origem_propria" />
                  )
                }
              />
            )}
            <Column<ContratoNaLista>
              name="numero"
              label="Número"
              enableSorting
              render={(_v, contrato) => <span className="tabular-nums">{numeroComAno(contrato.numero, contrato.ano)}</span>}
            />
            <Column<ContratoNaLista>
              name="objeto"
              label="Objeto"
              enableSorting
              classe="min-w-[280px]"
              render={(_v, contrato) => <span className={TEXTO_DO_CADASTRO}>{contrato.objeto}</span>}
            />
            <Column<ContratoNaLista>
              name="situacao"
              label="Situação"
              enableSorting
              render={(_v, contrato) => <SeloDoValor valor={contrato.situacao} rotulo={rotulo(contrato.situacao)} />}
            />
            <Column<ContratoNaLista>
              name="vigencia_fim"
              label="Vigência até"
              enableSorting
              render={(_v, contrato) => <span className="tabular-nums">{data(contrato.vigencia_fim)}</span>}
            />
            <Column<ContratoNaLista>
              name="valor_atual"
              label="Valor atual"
              enableSorting
              classe={COLUNA_NUMERICA}
              render={(_v, contrato) => <span className="block text-center tabular-nums">{moeda(contrato.valor_atual)}</span>}
            />
            <Column<ContratoNaLista>
              name="contratado"
              label="Contratado"
              enableSorting={false}
              render={(_v, contrato) => <span className={TEXTO_DO_CADASTRO}>{contrato.contratado.razao_social}</span>}
            />
          </TablePrincipal2>
        )}
      </CardContent>
    </Card>
  )
}

function ContratosEmCartoes({
  dados,
  mostrarOrigem,
  aoAbrirContrato,
}: {
  dados: ContratoNaLista[]
  mostrarOrigem: boolean
  aoAbrirContrato: (id: number) => void
}) {
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {dados.map((contrato) => (
        <li key={contrato.id}>
          <button
            type="button"
            data-slot="contrato-cartao"
            className="flex min-h-8 w-full cursor-pointer flex-col gap-1.5 rounded-md border border-border bg-card p-3 text-left hover:border-(--borda-forte) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            onClick={() => aoAbrirContrato(contrato.id)}
          >
            <span className="flex flex-wrap items-center gap-2">
              <strong className="text-(length:--texto-md) text-primary">{numeroComAno(contrato.numero, contrato.ano)}</strong>
              <SeloDoValor valor={contrato.situacao} rotulo={rotulo(contrato.situacao)} />
              {mostrarOrigem &&
                (contrato.origem === "athus" ? <Selo situacao="origem_athus" /> : <Selo situacao="origem_propria" />)}
            </span>
            <span className={cn("text-(length:--texto-sm) text-foreground", TEXTO_DO_CADASTRO)}>{contrato.objeto}</span>
            <span className="flex flex-wrap gap-x-4 gap-y-0.5 text-(length:--texto-xs) text-muted-foreground">
              <span className={TEXTO_DO_CADASTRO}>{contrato.contratado.razao_social}</span>
              <span>até {data(contrato.vigencia_fim)}</span>
              <span className="tabular-nums">{moeda(contrato.valor_atual)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
